import {
  Grid,
  Button,
  IconButton,
  Typography,
  ThemeProvider,
  useTheme
} from "@mui/material";
import Warning from "@mui/icons-material/Warning";

import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

import {
  ToggleButtons,
  DefaultPaper,
  TextField
} from "@aclymatepackages/atoms";
import mainTheme from "@aclymatepackages/themes";
import { formatDecimal } from "@aclymatepackages/formatters";
import { batteryTypes } from "@aclymatepackages/products";

import BatteryTypesSelect from "./BatteryTypesSelect";

import NumberInput from "../../atoms/inputs/NumberInput";

import { calculateMaterialsInputWeightsSum } from "../../../helpers/components/products";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const MaterialRow = ({
  item,
  index,
  editItem,
  items,
  removeItem,
  MaterialComponent,
  label,
  noLengthEdit = false,
  productWeightUnit,
  disabled,
  footprintShared
}) => {
  const {
    type,
    defaultWeight,
    weight,
    weightUnit,
    footprintKgs,
    label: materialLabel,
    isElectronics
  } = item;

  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const nonElectronicItems = items.filter(
    ({ isElectronics }) => !isElectronics
  );

  const findDisplayComponent = () => {
    const helperTextObj =
      footprintKgs !== undefined && type
        ? {
            helperText: `Footprint: ${
              isSubscribed || footprintShared
                ? formatDecimal(footprintKgs)
                : "####"
            } kgs CO2e`
          }
        : {};

    if (batteryTypes.find(({ type: batteryType }) => batteryType === type)) {
      return (
        <BatteryTypesSelect
          batteryType={type}
          setBatteryType={(value) => editItem(index, "type", value)}
          size="small"
          {...helperTextObj}
        />
      );
    }

    if (type === "electronics") {
      return <TextField value="Electronics" disabled {...helperTextObj} />;
    }

    if (!MaterialComponent) {
      return <Typography variant="h4">{materialLabel}</Typography>;
    }

    return (
      <MaterialComponent
        label={label}
        value={type}
        setValue={(value) => editItem(index, "type", value)}
        size="small"
        disabled={disabled}
        {...helperTextObj}
      />
    );
  };

  return (
    <Grid item>
      <Grid container justifyContent="space-between" wrap="nowrap">
        <Grid item xs={5}>
          {findDisplayComponent()}
        </Grid>
        <Grid item xs={3}>
          <NumberInput
            label="Weight"
            value={weight || defaultWeight || ""}
            setValue={(value) => {
              editItem(index, "defaultWeight", null);
              return editItem(index, "weight", value);
            }}
            size="small"
            disabled={disabled}
            error={
              (weightUnit === "%" && weight > 100) ||
              (!weight && !defaultWeight)
            }
          />
        </Grid>
        <Grid item>
          <ToggleButtons
            value={weightUnit}
            buttons={[productWeightUnit, "%"].map((unit) => ({
              value: unit,
              name: unit
            }))}
            onChange={(value) => editItem(index, "weightUnit", value)}
            disabled={disabled}
          />
          <Typography variant="caption" display="block" align="center">
            Unit
          </Typography>
        </Grid>
        <Grid item>
          {nonElectronicItems.length > 1 && !noLengthEdit && (
            <IconButton
              onClick={() => removeItem(index)}
              size="small"
              color="error"
              disabled={isElectronics || disabled}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const MaterialsInterface = ({
  items = [],
  setItems,
  productWeightUnit,
  type,
  totalWeight,
  MaterialComponent,
  noLengthEdit = false,
  sortingFunction = (array) => array,
  disabled,
  footprintShared
}) => {
  const { palette } = useTheme();

  const editItem = (index, field, value) => {
    const base = items.length
      ? items
      : [{ type: null, weight: null, weightUnit: productWeightUnit }];
    setItems(
      base.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addItem = () => {
    return setItems([
      ...items,
      {
        type: null,
        weight: null,
        weightUnit: items[items.length - 1]?.weightUnit || productWeightUnit
      }
    ]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, idx) => idx !== index));
    }
  };

  const weightSum = calculateMaterialsInputWeightsSum(items, totalWeight);

  const buildItemsDisplayArray = () => {
    if (!items.length) {
      return [{ type: null, weight: null }];
    }
    return sortingFunction(items);
  };

  return (
    <Grid container spacing={2} direction="column">
      {buildItemsDisplayArray().map((item, index) => (
        <MaterialRow
          key={index}
          item={item}
          index={index}
          editItem={editItem}
          items={items}
          removeItem={removeItem}
          MaterialComponent={MaterialComponent}
          label={`Select a product ${
            type === "packaging" ? "packaging " : ""
          }material`}
          productWeightUnit={productWeightUnit}
          noLengthEdit={noLengthEdit}
          disabled={disabled}
          footprintShared={footprintShared}
        />
      ))}
      {!noLengthEdit && (
        <Grid item container justifyContent="center">
          <Grid item>
            <ThemeProvider theme={mainTheme}>
              <Button
                onClick={addItem}
                startIcon={<AddIcon />}
                size="small"
                color="secondary"
                disabled={disabled || !items[items.length - 1]?.type}
              >
                {type === "packaging"
                  ? "Add another packaging material"
                  : "Add another material"}
              </Button>
            </ThemeProvider>
          </Grid>
        </Grid>
      )}
      {Math.round(weightSum) !== Math.round(totalWeight) && (
        <Grid item>
          <DefaultPaper style={{ backgroundColor: palette.error.main }}>
            <Grid container spacing={2} alignItems="center" flexWrap="nowrap">
              <Grid item>
                <Warning style={{ color: "white", fontSize: 50 }} />
              </Grid>
              <Grid item>
                <Typography variant="subtitle1">
                  Your {type === "materials" ? "product" : "packaging"} weight
                  is{" "}
                  <strong>
                    {totalWeight} {productWeightUnit}
                  </strong>{" "}
                  but the total weight of your{" "}
                  {type === "packaging" && "packaging "}materials is{" "}
                  <strong>
                    {formatDecimal(weightSum)} {productWeightUnit}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </DefaultPaper>
        </Grid>
      )}
    </Grid>
  );
};
export default MaterialsInterface;
